import { HttpParams } from '@angular/common/http';
import { ClientLibrarySearchFilters } from '../../../../../models/client-library/client-library-search-filters';
import { ClientLibraryQueryState } from '../../../../../models/client-library/client-library-query.interface';
import { SortClientLibrary } from '../../../../../models/client-library/sort-client-library';

const DEFAULT_CLIENT_LIBRARY_PAGE_SIZE = 50;

const DYNAMIC_PARAM_KEYS = [
  'search',
  'limit',
  'offset',
  'order_by',
  'form_status',
  'form_title',
  'assigned_provider',
  'form_category',
  'shared_with_patient',
  'publication_date',
];

export class ClientLibraryQueryUtils {
  public static buildQueryParams(params: HttpParams, query: ClientLibraryQueryState): HttpParams {
    return ClientLibraryQueryUtils.applyQueryParams(params, query);
  }

  private static applyQueryParams(params: HttpParams, query: ClientLibraryQueryState): HttpParams {
    const filters = query.filters ?? new ClientLibrarySearchFilters({});
    const limit = ClientLibraryQueryUtils.normalizeLimit(query.limit);
    const offset = ClientLibraryQueryUtils.normalizeOffset(query.offset);

    const clearedParams = ClientLibraryQueryUtils.clearDynamicParams(params);
    const pagedParams = clearedParams.set('limit', String(limit)).set('offset', String(offset));
    const search = query.search?.trim();
    const searchedParams = search ? pagedParams.set('search', search) : pagedParams;
    const sortedParams = ClientLibraryQueryUtils.applySorting(searchedParams, query.orderBy);

    return ClientLibraryQueryUtils.applyFilters(sortedParams, filters);
  }

  private static clearDynamicParams(params: HttpParams): HttpParams {
    let currentParams = params;

    for (const key of DYNAMIC_PARAM_KEYS) {
      currentParams = currentParams.delete(key);
    }

    return currentParams;
  }

  private static applySorting(params: HttpParams, orderBy?: SortClientLibrary): HttpParams {
    switch (orderBy) {
      case SortClientLibrary.Name:
        return params.set('order_by', 'form_title');

      case SortClientLibrary.Date:
      default:
        return params.set('order_by', '-publication_date');
    }
  }

  private static applyFilters(params: HttpParams, filters: ClientLibrarySearchFilters): HttpParams {
    let currentParams = params;

    if (filters.title?.length) {
      for (const title of filters.title) {
        currentParams = currentParams.append('form_title', title);
      }
    }

    if (filters.assigned_provider?.length) {
      currentParams = currentParams.set('assigned_provider', filters.assigned_provider.join(','));
    }

    if (filters.form_group?.length) {
      currentParams = currentParams.set('form_category', filters.form_group.join(','));
    }

    if ((filters.shared_with_patient?.length ?? 0) === 1) {
      currentParams = currentParams.set(
        'shared_with_patient',
        String(filters.shared_with_patient[0]),
      );
    }

    if (filters.publication_date?.length) {
      currentParams = currentParams.set('publication_date', filters.publication_date[0]);
    }

    if (filters.status?.length) {
      currentParams = currentParams.set('form_status', filters.status.join(','));
    }

    return currentParams;
  }

  private static normalizeLimit(limit?: number): number {
    return typeof limit === 'number' && Number.isFinite(limit) && limit > 0
      ? limit
      : DEFAULT_CLIENT_LIBRARY_PAGE_SIZE;
  }

  private static normalizeOffset(offset?: number): number {
    return typeof offset === 'number' && Number.isFinite(offset) && offset >= 0 ? offset : 0;
  }
}
